import { formatDateTime } from '../../utils/format'

const STORAGE_ITEM_ID = 'logs.errors'
const ERROR_QUEUE_LIMIT_COUNT = 20
const ERROR_QUEUE_LIMIT_MINUTES = 10

const ERROR_QUEUE_TRIGGER_FREEZE_COUNT = 9

type ErrorLoggerType = {
  [errorId: string]: ErrorType
}

type ErrorType = {
  state: ErrorLoggerStateEnum
  logs: LogType[]
}

type LogType = {
  timestamp: string
  timestampISO: string
}

export enum ErrorLoggerStateEnum {
  'active' = 'active',
  'freezeStart' = 'Freeze Start',
  'freezed' = 'Freezed',
  'freezeEnd' = 'Freeze End',
}

const DEFAULT_ERROR: ErrorType = {
  state: ErrorLoggerStateEnum.active,
  logs: [],
}

export class ErrorLogger {
  //
  // Get all stored data
  //
  static getStoredData(): ErrorLoggerType {
    try {
      const raw = sessionStorage.getItem(STORAGE_ITEM_ID)
      return JSON.parse(raw || '{}') as ErrorLoggerType
    } catch {
      return {}
    }
  }

  //
  // Get stored data by errorId
  //
  static getStoredError(errorId: string): ErrorType {
    return this.getStoredData()[errorId] || null
  }

  //
  // Store all data
  //
  static storeData(data: ErrorLoggerType): void {
    sessionStorage.setItem(STORAGE_ITEM_ID, JSON.stringify(data))
  }

  //
  // Get updated error queue with time checks
  //
  static getCleanLogQueue(error: ErrorType): LogType[] {
    let queue: LogType[] = error?.logs || []

    // Delete logs older than ERROR_QUEUE_LIMIT_MINUTES
    const now = Date.now()
    const cutoff = now - ERROR_QUEUE_LIMIT_MINUTES * 60 * 1000

    const firstOldIndex = queue.findIndex(log => {
      const time = Date.parse(log.timestampISO)
      return isNaN(time) || time < cutoff
    })

    if (firstOldIndex >= 0) {
      queue = queue.slice(0, firstOldIndex)
    }

    return queue
  }

  //
  // Check error state
  //
  static getErrorState(error: ErrorType): ErrorLoggerStateEnum {
    if (!error || !error?.logs?.length) {
      return ErrorLoggerStateEnum.active
    }

    const tooManyErrors = error.logs.length >= ERROR_QUEUE_TRIGGER_FREEZE_COUNT
    const prevState = error.state

    if (tooManyErrors) {
      if (prevState === ErrorLoggerStateEnum.active) {
        // Too many errors for the first time -> freeze start
        return ErrorLoggerStateEnum.freezeStart
      }
      // Too many errors already -> freezed
      return ErrorLoggerStateEnum.freezed
    }
    if (prevState === ErrorLoggerStateEnum.freezed) {
      // Not enough errors to freeze while frozen -> freeze end
      return ErrorLoggerStateEnum.freezeEnd
    }
    // Not enough errors to freeze
    return ErrorLoggerStateEnum.active
  }

  //
  // Store error log and return current error data
  //
  static storeErrorLog(errorId: string, errorLog: LogType): ErrorType {
    const data = this.getStoredData()

    if (!data[errorId]) {
      data[errorId] = { ...DEFAULT_ERROR }
    }

    if (data[errorId]?.logs?.length) {
      // Add error to queue and limit its size
      data[errorId].logs = [errorLog, ...data[errorId].logs].slice(0, ERROR_QUEUE_LIMIT_COUNT)
    } else {
      // Start a new queue
      data[errorId].logs = [errorLog]
    }

    // Store in local storage and return Error data
    this.storeData(data)
    return data[errorId]
  }

  //
  // Cleanup all errors (to throw away all old errors and keep space requirements low)
  //
  static cleanupErrors(): ErrorLoggerType {
    const data = this.getStoredData()
    Object.entries(data).forEach(([errorId, errorData]) => {
      const cleanLogs = this.getCleanLogQueue(errorData)
      if (cleanLogs?.length) {
        data[errorId].logs = this.getCleanLogQueue(errorData)
        data[errorId].state = this.getErrorState(errorData)
      } else {
        delete data[errorId]
      }
    })

    this.storeData(data)
    return data
  }

  //
  // Add new error log and update all errors (space management)
  //
  static addLog(errorId: string): ErrorLoggerType {
    const now = new Date()
    const newLog = {
      timestamp: formatDateTime(now),
      timestampISO: now.toISOString(),
    }

    this.storeErrorLog(errorId, newLog)
    const updatedErrorsData = this.cleanupErrors()
    return updatedErrorsData
  }
}
